import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinnerThird, faUser, faClock, faCheck, faTimes, faCommentAlt } from "@fortawesome/pro-solid-svg-icons";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import NotificationToast from "../Helpers/NotificationToast";

const CreditCheckQueue = ({ currentStaff, onChatClick }) => {
    const [loading, setLoading] = useState(true);
    const [queue, setQueue] = useState({
        in_progress: [],
        pending_approval: [],
    });
    const [refreshing, setRefreshing] = useState(false);
    const [denyReason, setDenyReason] = useState('');
    const [denyingItemId, setDenyingItemId] = useState(null);
    const history = useHistory();



    const handleUserClick = (userId, bookingId) => {
        // Navigate to the booking page with user_id and booking_id as query parameters
        history.push({
            pathname: "/booking/residents",
            search: `?user_id=${userId}&booking_id=${bookingId}`,
        });
    };

    const handleChatClick = (item) => {
        if (onChatClick) {
            // If parent component provided a chat click handler, use it
            onChatClick(item);
        } else {
            // Navigate to the chat with user_id and booking_id as query parameters
            history.push({
                pathname: "/booking/residents",
                search: `?user_id=${item.user_id}&booking_id=${item.booking_id}`,
            });
        }
    };

    const fetchQueue = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}tpn-approval/queue`);
            if (response.data.success) {
                setQueue(response.data.data);
            } else {
                throw new Error(response.data.message || "Failed to fetch queue");
            }
        } catch (error) {
            console.error("Error fetching credit check queue:", error);
            toast(<NotificationToast title="Error" message={error.response?.data?.message || "Failed to load credit check queue"} type="error" />);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleApprove = async (id) => {
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}tpn-approval/${id}/approve`);
            if (response.data.success) {
                toast(<NotificationToast title="Success" message="Credit check approved successfully" type="success" />);
                fetchQueue();
            }
        } catch (error) {
            console.error("Error approving credit check:", error);
            toast(<NotificationToast title="Error" message={error.response?.data?.message || "Failed to approve credit check"} type="error" />);
        }
    };

    const handleDenyClick = (itemId) => {
        setDenyingItemId(denyingItemId === itemId ? null : itemId);
        setDenyReason('');
    };

    const handleDeny = async (item) => {
        const trimmedReason = denyReason.trim();
        if (!trimmedReason) {
            toast(<NotificationToast title="Error" message="Please provide a reason for denial" type="error" />);
            return;
        }

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}tpn-approval/${item.id}/deny`, {
                denial_reason: trimmedReason,
            });

            if (response.data.success) {
                toast(<NotificationToast title="Success" message="Credit check has been denied" type="success" />);
                setDenyingItemId(null);
                setDenyReason('');
                fetchQueue();
            }
        } catch (error) {
            console.error("Error denying credit check:", error);
            toast(<NotificationToast title="Error" message={error.response?.data?.message || "Failed to deny credit check"} type="error" />);
        }
    };

    const refreshQueue = () => {
        setRefreshing(true);
        fetchQueue();
    };

    useEffect(() => {
        fetchQueue();
    }, []);

    const renderQueueItem = (item, index) => {
        const staffId = Number(currentStaff?.id ?? 0);
        const assignedToId = Number(item.assigned_to ?? 0);
        const isSuperUser = staffId === 1;
        const isAssignedToMe = assignedToId === staffId && staffId > 0;
        const isInProgress = item.status === "in_progress";
        const canActOnItem = isInProgress && (isAssignedToMe || isSuperUser);

        return (
            <div key={`${item.id}-${index}`} className={`p-4 mb-2 rounded-lg border ${isInProgress ? "border-yellow-400 bg-yellow-50" : "border-gray-200 bg-white"}`}>
                <div className="space-y-3">
                    <div className="flex justify-between items-start">
                        <div className="flex-1">
                            <div className="flex items-start justify-between">
                                <div className="flex items-center">
                                    <FontAwesomeIcon icon={isInProgress ? faUser : faClock} className={`mr-2 ${isInProgress ? "text-yellow-500" : "text-gray-500"}`} />
                                    <div className="flex flex-col">
                                        <span className="font-medium hover:text-blue-600 cursor-pointer" onClick={() => handleUserClick(item.user_id, item.booking_id)}>
                                            {item.user?.name + " " + item.user?.surname || "Unknown User"}
                                        </span>
                                        {item.booking && <span className="text-xs text-gray-500">Booking: {item.booking.booking_reference || "No Booking Link"}</span>}
                                    </div>
                                </div>
                                {/* Chat Button */}
                                <button onClick={() => handleChatClick(item)} className="text-blue-500 hover:text-blue-700 ml-2" title="Open chat">
                                    <FontAwesomeIcon icon={faCommentAlt} />
                                </button>
                            </div>
                            <div className="text-sm text-gray-600 ml-4 mt-1">
                                {item.assigned_to && (
                                    <div>
                                        Assigned to: {' '}
                                        <span className="font-medium hover:text-blue-600 cursor-pointer" onClick={() => handleUserClick(item.assigned_to)}>
                                            {item.assignee?.name + " " + item.assignee?.surname || "Unassigned"}
                                        </span>
                                    </div>
                                )}
                                {item.status_updated_at && <div>Last updated: {new Date(item.status_updated_at).toLocaleString()}</div>}
                            </div>
                        </div>
                    </div>

                    {canActOnItem && (
                        <div className="space-y-2 pt-2 border-t border-gray-100">
                            <div className="flex justify-end space-x-2">
                                <button 
                                    onClick={() => handleApprove(item.id)} 
                                    className="px-3 py-1.5 bg-green-500 text-white rounded hover:bg-green-600 text-sm flex items-center transition-colors"
                                >
                                    <FontAwesomeIcon icon={faCheck} className="mr-1" />
                                    Approve
                                </button>
                                <button
                                    onClick={() => handleDenyClick(item.id)}
                                    className="px-3 py-1.5 bg-red-500 text-white rounded hover:bg-red-600 text-sm flex items-center transition-colors"
                                >
                                    <FontAwesomeIcon icon={faTimes} className="mr-1" />
                                    {denyingItemId === item.id ? 'Cancel' : 'Deny'}
                                </button>
                            </div>
                            {denyingItemId === item.id && (
                                <div className="w-full">
                                    <textarea
                                        className="w-full border border-gray-300 rounded-md p-2 mb-2 h-24 focus:ring-blue-500 focus:border-blue-500 resize-none text-sm"
                                        placeholder="Enter reason for denial..."
                                        value={denyReason}
                                        onChange={(e) => setDenyReason(e.target.value)}
                                        autoFocus
                                    />
                                    <div className="flex justify-end space-x-2">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setDenyingItemId(null);
                                                setDenyReason('');
                                            }}
                                            className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleDeny(item)}
                                            className="px-3 py-1 text-xs font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                                        >
                                            Submit Denial
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {item.denial_reason && (
                    <div className="mt-2 p-2 bg-red-50 border border-red-100 rounded text-sm text-red-700">
                        <strong>Denial Reason:</strong> {item.denial_reason}
                    </div>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-40">
                <FontAwesomeIcon icon={faSpinnerThird} spin className="text-2xl text-blue-500" />
            </div>
        );
    }



    return (
        <div className="p-4">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Credit Check Queue</h2>
                <button 
                    onClick={refreshQueue} 
                    disabled={refreshing} 
                    className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm flex items-center"
                >
                    {refreshing ? (
                        <>
                            <FontAwesomeIcon icon={faSpinnerThird} spin className="mr-1" />
                            Refreshing...
                        </>
                    ) : 'Refresh'}
                </button>
            </div>

            <div className="mb-6">
                <h3 className="text-md font-medium mb-2 flex items-center">
                    <FontAwesomeIcon icon={faUser} className="text-yellow-500 mr-2" />
                    In Progress
                </h3>
                {queue.in_progress.length > 0 ? (
                    queue.in_progress.map((item, index) => renderQueueItem(item, index))
                ) : (
                    <div className="text-gray-500 text-center py-4">No credit checks in progress</div>
                )}
            </div>

            <div>
                <h3 className="text-md font-medium mb-2 flex items-center">
                    <FontAwesomeIcon icon={faClock} className="text-gray-500 mr-2" />
                    Pending Approval
                </h3>
                {queue.pending_approval.length > 0 ? (
                    queue.pending_approval.map((item, index) => renderQueueItem(item, index))
                ) : (
                    <div className="text-gray-500 text-center py-4">No pending approval requests</div>
                )}
            </div>
        </div>
    );
};

export default CreditCheckQueue;
